import { unwrap } from "@sagot/api-client";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PasswordInput } from "../../../../auth/components/password-input";
import { useErrorMessage } from "../../../../lib/api-errors";
import { zodResolver } from "../../../../lib/zod-resolver";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { wsKeys } from "../../shared";
import { CardActions } from "./panel-kit";

function usePasswordSchema() {
  const { t } = useTranslation();
  return z
    .object({
      current: z.string().min(1, t("settings.password.currentRequired")).max(PASSWORD_MAX_LENGTH),
      next: z.string().min(PASSWORD_MIN_LENGTH, t("settings.password.tooShort", { count: PASSWORD_MIN_LENGTH })).max(PASSWORD_MAX_LENGTH),
      confirm: z.string(),
    })
    .refine((v) => v.next === v.confirm, { path: ["confirm"], message: t("settings.password.mismatch") });
}
type PasswordValues = z.infer<ReturnType<typeof usePasswordSchema>>;

/** Change password (`POST /v1/me/password`); the API signs out the member's other sessions. */
export function PasswordCard() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const readOnly = usePermissions().readOnly;
  const { register, handleSubmit, reset, formState } = useForm<PasswordValues>({
    resolver: zodResolver(usePasswordSchema()),
    defaultValues: { current: "", next: "", confirm: "" },
  });
  const onSubmit = handleSubmit(async (v) => {
    try {
      await unwrap(api.POST("/v1/me/password", { body: { current_password: v.current, new_password: v.next } }));
      reset();
      toast.success(t("settings.password.changed"));
      await queryClient.invalidateQueries({ queryKey: wsKeys.sessions });
    } catch (caught) {
      toast.error(message(caught));
    }
  });
  return (
    <form className="card" onSubmit={(event) => void onSubmit(event)} noValidate aria-labelledby="password-title">
      <div className="card-header">
        <h2 id="password-title" className="card-title">
          {t("settings.password.title")}
        </h2>
      </div>
      <fieldset className="card-body grid grid-cols-1 gap-4 md:grid-cols-3" disabled={readOnly}>
        <Field label={t("settings.password.current")} error={formState.errors.current?.message}>
          <PasswordInput autoComplete="current-password" {...register("current")} />
        </Field>
        <Field label={t("settings.password.new")} error={formState.errors.next?.message}>
          <PasswordInput autoComplete="new-password" placeholder={t("settings.password.newPlaceholder", { count: PASSWORD_MIN_LENGTH })} {...register("next")} />
        </Field>
        <Field label={t("settings.password.confirm")} error={formState.errors.confirm?.message}>
          <PasswordInput autoComplete="new-password" {...register("confirm")} />
        </Field>
      </fieldset>
      <CardActions>
        <Button type="submit" variant="secondary" disabled={readOnly} loading={formState.isSubmitting}>
          {t("settings.password.submit")}
        </Button>
      </CardActions>
    </form>
  );
}
